import { useEffect, useState } from 'react';
import { Navigate, useSearchParams, useNavigate } from 'react-router-dom';
import Loader from './Loader';
import useAuth from 'hooks/useAuth';
import { useGetReservation, useGetLastReservation } from 'api/reservations';
import { Box, Button, Typography, Container, Stack } from '@mui/material';
import { HomeOutlined, WarningOutlined } from '@ant-design/icons';
import { useTranslation } from 'hooks/useTranslation';
import axiosServices from 'utils/axios';

export enum RedirectPath {
  HOME = '/',
  FEEDBACK = 'feedback',
  SELECT_WAITER = 'select-waiter',
  MY_REQUESTS = 'my-requests',
  SET_PASSWORD = 'set-password'
}

interface IProps {
  redirectPath?: RedirectPath;
  loginWithEmail?: boolean;
}

function WorkerInviteRedirect({ redirectPath = RedirectPath.HOME, loginWithEmail = false }: IProps) {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const accessToken = token;
  const navigate = useNavigate();
  const [reservationId, setReservationId] = useState<string | null>(null);
  const [isLoadingProfile, setIsLoadingProfile] = useState(false);
  const { reservation, reservationLoading } = useGetReservation(reservationId);
  const { getLastReservation, isLoading: lastReservationLoading } = useGetLastReservation();
  const { loginWithPhone, isLoggedIn } = useAuth();
  const [loginError, setLoginError] = useState<string | null>(null);

  const t = useTranslation();

  useEffect(() => {
    if (!accessToken) {
      return;
    }

    if (!isLoggedIn) {
      setIsLoadingProfile(true);
      const originalAuth = axiosServices.defaults.headers.common['Authorization'];
      axiosServices.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;

      axiosServices
        .get('/users/me')
        .then(async (response) => {
          const userData = response.data?.data;
          const userId = userData?.id;
          const role = userData?.role;
          const abilities = userData?.abilities;
          const waiterId = userData?.waiter_id;
          const restaurantId = userData?.restaurant_ids?.[0] || userData?.waiter?.restaurant_id;

          if (abilities?.reservation_id) {
            setReservationId(abilities.reservation_id);
          } else if (redirectPath === RedirectPath.SELECT_WAITER && restaurantId) {
            try {
              const lastRes = await getLastReservation(restaurantId);
              if (lastRes.success && lastRes.data?.id) {
                setReservationId(lastRes.data.id);
              }
            } catch (error) {
              console.error('Failed to fetch latest reservation:', error);
            }
          }

          if (userId) {
            return loginWithPhone({
              accessToken,
              userId,
              role: role || 'customer',
              waiterId
            });
          }
        })
        .catch((error) => {
          console.error('Failed to authenticate with token:', error);
          setLoginError('Failed to authenticate with token');
          axiosServices.defaults.headers.common['Authorization'] = originalAuth;
        })
        .finally(() => {
          setIsLoadingProfile(false);
        });
    } else if (isLoggedIn && !reservationId) {
      setIsLoadingProfile(true);
      const originalAuth = axiosServices.defaults.headers.common['Authorization'];
      axiosServices.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;

      axiosServices
        .get('/users/me')
        .then(async (response) => {
          const userData = response.data?.data;
          const abilities = userData?.abilities;
          const restaurantId = userData?.restaurant_ids?.[0] || userData?.waiter?.restaurant_id || userData?.abilities?.restaurant_id;

          if (abilities?.reservation_id) {
            setReservationId(abilities.reservation_id);
          } else if (redirectPath === RedirectPath.SELECT_WAITER && restaurantId) {
            try {
              const lastRes = await getLastReservation(restaurantId);
              if (lastRes.success && lastRes.data?.id) {
                setReservationId(lastRes.data.id);
              } else {
                console.warn('No latest reservation found or failed to fetch');
                setIsLoadingProfile(false);
              }
            } catch (error) {
              console.error('Failed to fetch latest reservation:', error);
              setIsLoadingProfile(false);
            }
          } else {
            console.warn('No reservation_id found in token abilities');
            setIsLoadingProfile(false);
          }
        })
        .catch((error) => {
          console.error('Failed to get user profile with token:', error);
          setIsLoadingProfile(false);
        })
        .finally(() => {
          axiosServices.defaults.headers.common['Authorization'] = originalAuth;
        });
    }
  }, [accessToken, isLoggedIn, loginWithPhone, reservationId, getLastReservation, redirectPath]);

  if (redirectPath === RedirectPath.SET_PASSWORD) {
    if (!token) {
      return (
        <Container maxWidth="sm">
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: '100vh',
              textAlign: 'center',
              gap: 3
            }}
          >
            <WarningOutlined style={{ fontSize: 64, color: '#ff4d4f' }} />
            <Stack spacing={1}>
              <Typography variant="h3" color="textPrimary">
                {t('Something went wrong')}
              </Typography>
              <Typography variant="body1" color="textSecondary">
                {t('Token is missing.')} {t('Please try again or go to the home page.')}
              </Typography>
            </Stack>
            <Button variant="contained" color="primary" startIcon={<HomeOutlined />} onClick={() => navigate('/')} size="large">
              {t('Go to Home')}
            </Button>
          </Box>
        </Container>
      );
    }
    return <Navigate to={`/auth/set-password?token=${token}`} replace />;
  }

  if (isLoadingProfile) {
    return <Loader />;
  }

  if (reservationLoading && reservationId && lastReservationLoading) {
    return <Loader />;
  }

  // Handle errors
  if ((loginError || !accessToken) && !isLoadingProfile) {
    return (
      <Container maxWidth="sm">
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '100vh',
            textAlign: 'center',
            gap: 3
          }}
        >
          <WarningOutlined style={{ fontSize: 64, color: '#ff4d4f' }} />
          <Stack spacing={1}>
            <Typography variant="h3" color="textPrimary">
              {t('Something went wrong')}
            </Typography>
            <Typography variant="body1" color="textSecondary">
              {loginError || t('Token is missing.')} {t('Please try again or go to the home page.')}
            </Typography>
          </Stack>
          <Button variant="contained" color="primary" startIcon={<HomeOutlined />} onClick={() => navigate('/')} size="large">
            {t('Go to Home')}
          </Button>
        </Box>
      </Container>
    );
  }

  if (reservation) {
    console.log('reservation', reservation);
    if (redirectPath === RedirectPath.FEEDBACK) {
      const waiterId = reservation.waiter?.id;
      const restaurantId = reservation.restaurant?.id;

      return <Navigate to={`/feedback/${waiterId}?reservation_id=${reservation.id}&restaurant_id=${restaurantId}`} replace />;
    }

    if (redirectPath === RedirectPath.SELECT_WAITER) {
      const reservationDate = new Date(reservation.starts_at).toISOString().split('T')[0];
      const reservationTime = new Date(reservation.starts_at).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
      const reservationPartySize = reservation.party_size;
      const restaurantSlug = reservation.restaurant?.slug;

      return (
        <Navigate
          to={`/add-reservation?date=${reservationDate}&time=${reservationTime}&pax=${reservationPartySize}&restaurant-slug=${restaurantSlug}`}
          replace
        />
      );
    }
  }

  if (redirectPath === RedirectPath.SELECT_WAITER && !reservationId && !isLoadingProfile && !lastReservationLoading) {
    return (
      <Container maxWidth="sm">
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '100vh',
            textAlign: 'center',
            gap: 3
          }}
        >
          <WarningOutlined style={{ fontSize: 64, color: '#ff4d4f' }} />
          <Stack spacing={1}>
            <Typography variant="h3" color="textPrimary">
              {t('Reservation not found')}
            </Typography>
            <Typography variant="body1" color="textSecondary">
              {t('We could not find the reservation associated with this link.')} {t('Please try again or go to the home page.')}
            </Typography>
          </Stack>
          <Button variant="contained" color="primary" startIcon={<HomeOutlined />} onClick={() => navigate('/')} size="large">
            {t('Go to Home')}
          </Button>
        </Box>
      </Container>
    );
  }

  // Once we have data or are logged in, redirect to main application flow
  // RoleBasedRedirect at '/' will handle routing to correct dashboard
  if (isLoggedIn) {
    if (redirectPath === RedirectPath.HOME) {
      return <Navigate to="/" replace />;
    }

    if (redirectPath === RedirectPath.MY_REQUESTS) {
      return <Navigate to="/waiter/requests" replace />;
    }

    if (redirectPath === RedirectPath.FEEDBACK && !reservationId) {
      console.warn('Reservation ID not found in token abilities, redirecting to home');
      return <Navigate to="/" replace />;
    }
  }

  if (!accessToken && !isLoggedIn) {
    return <Navigate to={loginWithEmail ? '/customer/login/email' : '/customer/login'} replace />;
  }

  return <Loader />;
}

export default WorkerInviteRedirect;
